import { runInAction } from "mobx";
import { ICommand, RelayCommand } from "Application";
import { FieldType } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import moment from "moment";

import { WeeklyRotaDetailsViewModel } from "../WeeklyRotaDetailViewModel";
import { WeeklyRotaDetailModel } from "../WeeklyRotaDetailModel";
import { MentorAvailability } from "../Types";

export class CommandsManager {
    constructor(private viewModel: WeeklyRotaDetailsViewModel) {}

    // ─── Modal toggle commands ────────────────────────────────────────────────

    public toggleWeeklyRotaModalCommand: ICommand = new RelayCommand(() => {
        const { mobx } = this.viewModel.managers;
        mobx.setWeeklyRotaModalOpen(!mobx.weeklyRotaModalOpen);
    });

    public showMentorAbsenceModalCommand: ICommand = new RelayCommand(() => {
        const { mobx } = this.viewModel.managers;
        mobx.setMentorAbsenceModalOpen(!mobx.mentorAbsenceModalOpen);
    });

    public showSessionLeftModalCommand: ICommand = new RelayCommand(() => {
        const { mobx } = this.viewModel.managers;
        mobx.setSessionLeftModalOpen(!mobx.sessionLeftModalOpen);
    });

    // ─── Date navigation commands ─────────────────────────────────────────────

    public updateRotaStartDateCommand: ICommand = new RelayCommand((value: Date) => {
        this.updateField("startDate", moment(value).format("YYYY-MM-DD"));
    });

    public updateRotaDateRangeCommand: ICommand = new RelayCommand((value: Date) => {
        this.updateField("startDate", value.toISOString());
        this.viewModel.managers.api.getRotaByDateAsync(true);
    });

    public updateNextDateCommand: ICommand = new RelayCommand(() => {
        this.updateField("startDate", moment(this.viewModel.model.startDate).add(1, "week").startOf("isoWeek").add(1, "day").format("MM-DD-YYYY"));
        this.viewModel.managers.api.getRotaByDateAsync(true);
    });

    public updatePreviousDateCommand: ICommand = new RelayCommand(() => {
        this.updateField("startDate", moment(this.viewModel.model.startDate).add(-1, "week").startOf("isoWeek").add(1, "day").format("MM-DD-YYYY"));
        this.viewModel.managers.api.getRotaByDateAsync(true);
    });

    // ─── View mode commands ───────────────────────────────────────────────────

    public showEditableScheduleCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setRotaEditable(!this.viewModel.managers.mobx.rotaEditable);
    });

    public showResetRotaConfirmationCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setResetRotaConfirmationOpen(true);
    });

    public closeResetRotaConfirmationCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setResetRotaConfirmationOpen(false);
    });

    public resetRotaCommand: ICommand = new RelayCommand(async () => {
        this.viewModel.managers.mobx.setResetRotaConfirmationOpen(false);
        await this.viewModel.managers.api.resetRota();
    });

    // ─── Navigation commands ──────────────────────────────────────────────────

    public navigateToDetailCommand: ICommand = new RelayCommand((id: string) => {
        this.viewModel.history.push(AppUrls.Client.Students.Student.StudentDetail.Edit.replace(":id", id));
    });

    public navigateToSessionDetailCommand: ICommand = new RelayCommand((reportId: string) => {
        this.viewModel.history.push(AppUrls.Client.Students.Student.StudentReports.Add.replace(":reportId", reportId));
    });

    // ─── Modal close commands ─────────────────────────────────────────────────

    public closeAddStudentModalCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setOpenAddStudentModal(false);
    });

    public closeOverTimeConfirmationModalCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setOpenOverTimeConfirmationModal(false);
    });

    public closeStudentDetailModalCommand: ICommand = new RelayCommand(() => {
        this.viewModel.managers.mobx.setOpenStudentDetailModal(false);
    });

    /** Closes the OverTime confirmation popup and opens the Add Student modal. */
    public openAddStudentAfterOverTimeCommand: ICommand = new RelayCommand(() => {
        const { mobx } = this.viewModel.managers;
        mobx.setOpenOverTimeConfirmationModal(false);
        mobx.setOpenAddStudentModal(true);
    });

    // ─── ScheduleChart interaction commands ──────────────────────────────────

    /**
     * Opens the Add Student modal for a given slot.
     * Used when the slot is available and has no student yet.
     */
    public openAddStudentModalCommand: ICommand = new RelayCommand((params: { mentorId: string; sessionKey: string }) => {
        const { addStudentViewModel } = this.viewModel;
        const { mobx } = this.viewModel.managers;
        runInAction(() => {
            addStudentViewModel.model.mentorId = params.mentorId;
            addStudentViewModel.model.studentSessionId = params.sessionKey;
            mobx.setSessionKey(params.sessionKey);
            mobx.setMentorStudentAdded(false);
            mobx.setSuccessfullyGetWeeklyRota(false);
            mobx.setOpenAddStudentModal(true);
        });
    });

    /**
     * Opens the Overtime Confirmation popup for a slot that is outside normal availability.
     */
    public openOverTimeConfirmationModalCommand: ICommand = new RelayCommand((params: { mentorId: string; sessionKey: string }) => {
        const { addStudentViewModel } = this.viewModel;
        const { mobx } = this.viewModel.managers;

        runInAction(() => {
            addStudentViewModel.model.mentorId = params.mentorId;
            addStudentViewModel.model.studentSessionId = params.sessionKey;
            mobx.setSessionKey(params.sessionKey);
            mobx.setMentorStudentAdded(false);
            mobx.setSuccessfullyGetWeeklyRota(false);
            mobx.setOpenOverTimeConfirmationModal(true);
        });
    });

    /**
     * Opens the Student Detail modal for an existing assignment.
     */
    public openStudentDetailModalCommand: ICommand = new RelayCommand(async (params: { mentorId: string; sessionKey: string; mentorStudentRotaId: string }) => {
        const { addStudentViewModel } = this.viewModel;
        const { mobx } = this.viewModel.managers;

        runInAction(() => {
            addStudentViewModel.model.mentorId = params.mentorId;
            addStudentViewModel.model.studentSessionId = params.sessionKey;
        });
        await this.viewModel.managers.api.getMentorStudentRotaById(params.mentorStudentRotaId);
        runInAction(() => {
            mobx.setOpenStudentDetailModal(true);
        });
    });

    /** Removes a student from a slot. */
    public deleteStudentMentorCommand: ICommand = new RelayCommand(async (params: { mentorStudentRotaId: Guid; studentSessionId: Guid }) => {
        await this.viewModel.managers.api.deleteStudentMentor(params.mentorStudentRotaId, params.studentSessionId);
    });

    /** Removes session detail fields from a slot (live mode). */
    public removeDetailsCommand: ICommand = new RelayCommand(async (studentSessionId: Guid) => {
        runInAction(() => {
            this.viewModel.managers.mobx.setSuccessfullyGetWeeklyRota(false);
        });
        await this.viewModel.managers.api.removeDetails(studentSessionId);
    });

    /**
     * Deletes the mentor absence that covers a given slot, restoring availability immediately.
     */
    public deleteMentorAbsenceForSlotCommand: ICommand = new RelayCommand(async (params: { key: string; startDate: any; mentor: MentorAvailability }) => {
        const absence = this.viewModel.managers.domain.findAbsenceForSlot(params.key, params.startDate, params.mentor);
        if (!absence) return;

        runInAction(() => {
            // Restore the slot's availability flag immediately for instant UI feedback
            (params.mentor as any)[params.key] = true;
            this.viewModel.managers.mobx.setSuccessfullyGetWeeklyRota(false);
        });

        await this.viewModel.managers.api.deleteMentorAbsence(absence.id);
    });

    // ─── Private helpers ──────────────────────────────────────────────────────

    private updateField(fieldName: keyof FieldType<WeeklyRotaDetailModel>, value: any): void {
        this.viewModel.setValue(fieldName, value);
        this.viewModel.isFieldValid(fieldName);
    }
}
